import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { loadExerciseDatabase, searchExerciseNames } from "../../utils/exerciseDatabase";

function newLine(idPrefix) {
  return {
    id: `${idPrefix}-${crypto.randomUUID()}`,
    depth: 0,
    text: "",
  };
}

/**
 * Workflowy-style nested list: Tab / Shift+Tab indent, Enter new row, Backspace on empty removes line.
 * When the exercise catalog loads, each line uses autocomplete + optional muscle-group filter.
 */
const ProgramOutlineEditor = ({
  lines,
  onChange,
  placeholder = "Workout title on first line — Tab to indent exercises and sets…",
}) => {
  const idPrefix = useRef(`ol-${Math.random().toString(36).slice(2)}`);
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [muscleFilter, setMuscleFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    loadExerciseDatabase()
      .then((data) => {
        if (!cancelled && data?.exercises?.length) setCatalog(data);
        else if (!cancelled) setCatalog(null);
      })
      .catch(() => {
        if (!cancelled) setCatalog(null);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setLines = useCallback(
    (updater) => {
      onChange((prev) =>
        typeof updater === "function" ? updater(prev) : updater
      );
    },
    [onChange]
  );

  /** Focus a row; only move the caret to the end when opening a new line (not on every focus). */
  const focusRow = useCallback((index, { moveCaretToEnd = false } = {}) => {
    requestAnimationFrame(() => {
      const el = document.getElementById(`${idPrefix.current}-input-${index}`);
      el?.focus?.();
      if (
        moveCaretToEnd &&
        el &&
        typeof el.setSelectionRange === "function"
      ) {
        const len = el.value?.length ?? 0;
        el.setSelectionRange(len, len);
      }
    });
  }, []);

  const handleChangeText = useCallback(
    (index, text) => {
      setLines((prev) => {
        const next = [...prev];
        if (!next[index]) return prev;
        next[index] = { ...next[index], text };
        return next;
      });
    },
    [setLines]
  );

  const doIndent = useCallback(
    (index, shiftKey) => {
      setLines((prev) => {
        const row = prev[index];
        if (!row) return prev;
        const next = [...prev];
        const delta = shiftKey ? -1 : 1;
        const newDepth = Math.max(0, Math.min(12, row.depth + delta));
        next[index] = { ...row, depth: newDepth };
        return next;
      });
    },
    [setLines]
  );

  const doInsertLineAfter = useCallback(
    (index) => {
      let insertAt = index + 1;
      let insertDepth = 0;
      setLines((prev) => {
        const row = prev[index];
        insertDepth = row?.depth ?? 0;
        const next = [...prev];
        next.splice(insertAt, 0, {
          id: `${idPrefix.current}-${crypto.randomUUID()}`,
          depth: insertDepth,
          text: "",
        });
        return next;
      });
      focusRow(insertAt, { moveCaretToEnd: true });
    },
    [setLines, focusRow]
  );

  const doRemoveLine = useCallback(
    (index) => {
      setLines((prev) => {
        if (prev.length <= 1) return prev;
        return prev.filter((_, i) => i !== index);
      });
      focusRow(Math.max(0, index - 1), { moveCaretToEnd: false });
    },
    [setLines, focusRow]
  );

  const handlePlainRowKeyDown = useCallback(
    (index, e) => {
      if (e.key === "Tab") {
        e.preventDefault();
        doIndent(index, e.shiftKey);
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        doInsertLineAfter(index);
        return;
      }

      if (e.key === "Backspace") {
        const row = lines[index];
        if (!row || row.text !== "") return;
        e.preventDefault();
        doRemoveLine(index);
      }
    },
    [lines, doIndent, doInsertLineAfter, doRemoveLine]
  );

  const handleCatalogRowKeyDown = useCallback(
    (index, e, muiKeyDown) => {
      if (e.key === "Tab") {
        e.preventDefault();
        doIndent(index, e.shiftKey);
        return;
      }

      if (e.key === "Backspace") {
        const row = lines[index];
        if (row && row.text === "") {
          e.preventDefault();
          doRemoveLine(index);
          return;
        }
      }

      if (e.key === "Enter") {
        const expanded = e.target?.getAttribute?.("aria-expanded") === "true";
        if (!expanded) {
          e.preventDefault();
          doInsertLineAfter(index);
          return;
        }
      }

      muiKeyDown?.(e);
    },
    [lines, doIndent, doInsertLineAfter, doRemoveLine]
  );

  const catalogOptionsFor = useCallback(
    (text) => {
      if (!catalog?.exercises?.length) return [];
      return searchExerciseNames(catalog, text, muscleFilter, 120).map(
        (ex) => ex.name
      );
    },
    [catalog, muscleFilter]
  );

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
        bgcolor: "action.hover",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        Outline: Tab / Shift+Tab indent · Enter new line (close the catalog
        list first, or pick with the mouse) · empty Backspace removes line ·
        put &quot;circuit&quot; in a depth-1 name for circuit blocks (depth 2
        = moves, depth 3+ = sets) · each depth-0 line after exercises starts
        another workout
      </Typography>

      {catalogLoading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading exercise catalog…
          </Typography>
        </Box>
      )}

      {!catalogLoading && catalog?.muscleGroups?.length > 0 && (
        <FormControl size="small" fullWidth sx={{ maxWidth: 360, mb: 1.5 }}>
          <InputLabel id={`${idPrefix.current}-muscle-label`}>
            Catalog muscle group
          </InputLabel>
          <Select
            labelId={`${idPrefix.current}-muscle-label`}
            label="Catalog muscle group"
            value={muscleFilter}
            onChange={(e) => setMuscleFilter(e.target.value)}
          >
            <MenuItem value="">
              <em>Any — all exercises</em>
            </MenuItem>
            {catalog.muscleGroups.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {lines.map((line, index) => {
        const options = catalogOptionsFor(line.text);
        const inputId = `${idPrefix.current}-input-${index}`;

        if (!catalog?.exercises?.length) {
          return (
            <Box
              key={line.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                pl: line.depth * 2.5,
                mb: 0.5,
              }}
              onMouseDown={(e) => {
                const input = document.getElementById(inputId);
                if (!input || input.contains(e.target)) return;
                e.preventDefault();
                input.focus();
              }}
            >
              <Typography
                component="span"
                sx={{
                  mt: "10px",
                  mr: 0.75,
                  color: "text.secondary",
                  fontSize: "0.65rem",
                  userSelect: "none",
                  flexShrink: 0,
                }}
              >
                •
              </Typography>
              <TextField
                id={inputId}
                size="small"
                fullWidth
                placeholder={index === 0 ? placeholder : ""}
                value={line.text}
                onChange={(e) => handleChangeText(index, e.target.value)}
                onKeyDown={(e) => handlePlainRowKeyDown(index, e)}
                variant="standard"
                InputProps={{ disableUnderline: false }}
                sx={{
                  "& .MuiInput-root": { fontSize: "0.95rem" },
                }}
              />
            </Box>
          );
        }

        return (
          <Box
            key={line.id}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              pl: line.depth * 2.5,
              mb: 0.5,
            }}
            onMouseDown={(e) => {
              const input = document.getElementById(inputId);
              if (!input || input.contains(e.target)) return;
              e.preventDefault();
              input.focus();
            }}
          >
            <Typography
              component="span"
              sx={{
                mt: "10px",
                mr: 0.75,
                color: "text.secondary",
                fontSize: "0.65rem",
                userSelect: "none",
                flexShrink: 0,
              }}
            >
              •
            </Typography>
            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              options={options}
              filterOptions={(opts) => opts}
              selectOnFocus={false}
              clearOnBlur={false}
              autoSelect={false}
              handleHomeEndKeys
              inputValue={line.text}
              onInputChange={(_e, newVal, reason) => {
                // Skip "reset"/"blur" — they refire on focus/click and jump the caret.
                if (
                  reason === "input" ||
                  reason === "clear" ||
                  reason === "selectOption"
                ) {
                  handleChangeText(index, newVal ?? "");
                }
              }}
              onChange={(_e, newVal) => {
                if (newVal != null) {
                  handleChangeText(index, String(newVal));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  placeholder={index === 0 ? placeholder : ""}
                  inputProps={{
                    ...params.inputProps,
                    id: inputId,
                    onMouseDown: (e) => {
                      // MUI Autocomplete root mousedown calls preventDefault and
                      // refocuses the input, which always moves the caret to the end.
                      e.stopPropagation();
                      params.inputProps?.onMouseDown?.(e);
                    },
                    onKeyDown: (e) =>
                      handleCatalogRowKeyDown(
                        index,
                        e,
                        params.inputProps?.onKeyDown
                      ),
                  }}
                  sx={{
                    "& .MuiInput-root": { fontSize: "0.95rem" },
                  }}
                />
              )}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default ProgramOutlineEditor;
export { newLine };
